import { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface Profile {
  id: string
  organization_id: string
  role: string
  full_name: string | null
  email: string
  phone?: string | null
  timezone?: string
  language?: string
  avatar_url?: string | null
  created_at: string
  last_login_at?: string | null
  permissions: any
  onboarded?: boolean
  onboarded_at?: string | null
}

interface Organization {
  id: string
  name: string
  slug: string
  website?: string
  industry?: string
  company_size?: string
  billing_email?: string
  subscription_status: string
  subscription_tier: string
  monthly_lead_limit: number
  monthly_leads_checked: number
  settings: any
}

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: Profile | null
  organization: Organization | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, fullName: string, orgName?: string) => Promise<void>
  signOut: () => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<void>
  refreshSession: () => Promise<void>
  canAccess: (permission: string) => boolean
  hasRole: (requiredRoles: string[]) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  
  // Load user data
  const loadUserData = async (currentUser: User) => {
    try {
      // Load profile with organization
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          organizations (
            id,
            name,
            slug,
            subscription_status,
            subscription_tier,
            monthly_lead_limit,
            monthly_leads_checked,
            settings
          )
        `)
        .eq('id', currentUser.id)
        .single()
      
      if (profileError) {
        console.error('Error loading profile:', profileError)
        return
      }
      
      if (profileData) {
        setProfile(profileData)
        setOrganization(profileData.organizations)
      }
    } catch (error) {
      console.error('Error loading user data:', error)
    }
  }
  
  // Initialize auth state
  useEffect(() => {
    let mounted = true
    
    // Check for demo mode first
    const isDemoMode = localStorage.getItem('demoMode') === 'true'
    if (isDemoMode) {
      const demoUserStr = localStorage.getItem('demoUser')
      if (demoUserStr) {
        const demoUser = JSON.parse(demoUserStr)
        setUser(demoUser as any)
        setProfile({
          id: demoUser.id,
          organization_id: 'demo-org',
          role: demoUser.role,
          full_name: demoUser.full_name,
          email: demoUser.email,
          created_at: new Date().toISOString(),
          permissions: { all: true },
          onboarded: true
        })
        setOrganization({
          id: 'demo-org',
          name: demoUser.organization,
          slug: 'demo-company',
          subscription_status: 'active',
          subscription_tier: 'enterprise',
          monthly_lead_limit: 10000,
          monthly_leads_checked: 2547,
          settings: {}
        })
        setLoading(false)
        return
      }
    }
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return
        
        setSession(session)
        setUser(session?.user ?? null)
        
        if (event === 'SIGNED_IN' && session?.user) {
          await loadUserData(session.user)
        } else if (event === 'SIGNED_OUT') {
          setProfile(null)
          setOrganization(null)
        }
        
        if (mounted) {
          setLoading(false)
        }
      }
    )
    
    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return
      
      setSession(session)
      setUser(session?.user ?? null)
      
      if (session?.user) {
        loadUserData(session.user).finally(() => {
          if (mounted) setLoading(false)
        })
      } else {
        if (mounted) setLoading(false)
      }
    })
    
    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])
  
  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      toast.success('Welcome back!')
      navigate('/dashboard')
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign in')
      throw error
    }
  }
  
  // Sign up with organization creation
  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    orgName?: string
  ) => {
    try {
      const redirectUrl = `${window.location.origin}/`
      
      // Create auth user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName
          }
        }
      })
      
      if (error) throw error
      if (!data.user) throw new Error('User creation failed')
      
      // If organization name provided, initialize organization
      if (orgName) {
        const { data: orgData, error: orgError } = await supabase.rpc(
          'initialize_organization',
          {
            p_org_name: orgName,
            p_owner_email: email,
            p_owner_id: data.user.id
          }
        )
        
        if (orgError) {
          console.error('Organization creation error:', orgError)
          throw new Error('Failed to create organization')
        }
      }
      
      toast.success('Account created! Please check your email to verify.')
      navigate('/auth/verify-email')
    } catch (error: any) {
      toast.error(error.message || 'Failed to create account')
      throw error
    }
  }
  
  // Sign out
  const signOut = async () => {
    try {
      // Clear demo mode on sign out
      localStorage.removeItem('demoMode')
      localStorage.removeItem('demoUser')
      localStorage.removeItem('mockMode')
      localStorage.removeItem('isAuthenticated')
      
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      setUser(null)
      setSession(null)
      setProfile(null)
      setOrganization(null)
      
      toast.success('Signed out successfully')
      navigate('/')
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign out')
      throw error
    }
  }
  
  // Update profile
  const updateProfile = async (updates: Partial<Profile>) => {
    if (!profile) return
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile.id)
        .select()
        .single()
      
      if (error) throw error
      
      setProfile(data)
      toast.success('Profile updated')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update profile')
      throw error
    }
  }
  
  // Refresh session
  const refreshSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error) throw error
      
      if (data.session) {
        setSession(data.session)
        setUser(data.user)
      }
    } catch (error) {
      console.error('Failed to refresh session:', error)
      await signOut()
    }
  }
  
  // Check permission-based access
  const canAccess = (permission: string): boolean => {
    if (!profile) return false
    return profile.permissions?.[permission] === true
  }
  
  // Check role-based access
  const hasRole = (requiredRoles: string[]): boolean => {
    if (!profile) return false
    return requiredRoles.includes(profile.role)
  }
  
  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        organization,
        loading,
        signIn,
        signUp,
        signOut,
        updateProfile,
        refreshSession,
        canAccess,
        hasRole
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}